import { api, LightningElement, wire } from "lwc";

import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { logger } from "@devforce/trailhead-client-logger";
import { BooleanAttr, attrToBool } from "common/helpers";

import * as locales from "./locales";

const SUCCESS_DURATION_MS = 3000;

// Supported languages for syntax highlighting
// By default, th-web will load this component with the java language,
// since content writers don't have a way to specify the language for code blocks
// via Badge Builder. We can extend this if/when Badge Builder adds support
// for specifying the language for code blocks.
type SupportedLanguages = "java";

const SUPPORTED_LANGUAGES = new Set<SupportedLanguages>(["java"]);

const isSupportedLanguage = (
  language: string | undefined
): language is SupportedLanguages => {
  return (
    language !== undefined &&
    SUPPORTED_LANGUAGES.has(language as SupportedLanguages)
  );
};

export default class extends LightningElement {
  @api language?: SupportedLanguages;
  @api lineNumbers: BooleanAttr = false;

  @wire(LabelsAdapter, { locales })
  private labels: LabelsAdapterValue = null!;

  private copySuccess = false;
  private hasHighlighted = false;
  private lastContent = "";
  private showToast = false;
  private successTimeoutId: number | null = null;
  private toastTimeoutId: number | null = null;

  private get codeClassName() {
    return this.language ? `language-${this.language}` : "";
  }

  private get content(): string {
    // Simply read from host textContent - this is set by the slot content
    return this.template.host.textContent?.trim() || "";
  }

  private get copyButtonLabel() {
    return this.copySuccess
      ? this.labels?.copySuccessButtonLabel
      : this.labels?.copyButtonLabel;
  }

  private get copyButtonClassName() {
    return this.copySuccess
      ? "text-block__copy-button text-block__copy-button--success"
      : "text-block__copy-button";
  }

  private get copyIconSymbol() {
    return this.copySuccess ? "check" : "copy";
  }

  private get needsLineNumbers() {
    return attrToBool(this.lineNumbers);
  }

  private get preClassName() {
    const classes = ["text-block__content"];
    if (this.needsLineNumbers) {
      classes.push("line-numbers");
    }
    return classes.join(" ");
  }

  renderedCallback() {
    const currentContent = this.content;

    // Only re-render if content has changed
    if (currentContent !== this.lastContent) {
      this.lastContent = currentContent;
      this.hasHighlighted = false;
      this.renderContent();
    }
  }

  private renderContent() {
    const preElement = this.template.querySelector("pre");
    if (!preElement) return;

    // Clear existing content
    preElement.innerHTML = "";

    // Create code element
    const codeElement = document.createElement("code");
    if (this.codeClassName) {
      codeElement.className = this.codeClassName;
    }
    codeElement.textContent = this.content;

    // Add code to pre
    preElement.appendChild(codeElement);

    // Apply syntax highlighting if language is supported
    if (isSupportedLanguage(this.language) && !this.hasHighlighted) {
      void this.applySyntaxHighlighting();
    }
  }

  private handleKeyDown(event: KeyboardEvent) {
    // Handle Cmd+C (Mac) or Ctrl+C (Windows/Linux) to copy
    if ((event.metaKey || event.ctrlKey) && event.key === "c") {
      event.preventDefault();
      void this.handleCopy();
    }
  }

  disconnectedCallback() {
    this.clearTimeouts();
  }

  private async applySyntaxHighlighting() {
    try {
      const { default: Prism } = await import("prismjs");

      // Import language support
      if (this.language === "java") {
        await import("prismjs/components/prism-java");
      }

      const codeElement = this.template.querySelector("code");
      const preElement = this.template.querySelector("pre");

      if (codeElement) {
        Prism.highlightElement(codeElement);

        // Manually add line numbers if needed (PrismJS plugin doesn't work in Shadow DOM)
        if (this.needsLineNumbers && preElement) {
          this.addLineNumbers(preElement, codeElement);
        }

        this.hasHighlighted = true;
      }
    } catch (error) {
      logger.error("Failed to apply syntax highlighting:", error);
    }
  }

  private addLineNumbers(preElement: Element, codeElement: Element) {
    // Remove existing line numbers if any
    preElement.querySelector(".line-numbers-rows")?.remove();

    // Count lines and create container
    const lineCount = (codeElement.textContent || "").split("\n").length;
    const lineNumbersRows = document.createElement("span");
    lineNumbersRows.setAttribute("aria-hidden", "true");
    lineNumbersRows.className = "line-numbers-rows";

    // Use DocumentFragment for better performance
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < lineCount; i++) {
      fragment.appendChild(document.createElement("span"));
    }
    lineNumbersRows.appendChild(fragment);

    preElement.appendChild(lineNumbersRows);
  }

  private clearTimeouts() {
    if (this.successTimeoutId) {
      clearTimeout(this.successTimeoutId);
      this.successTimeoutId = null;
    }
    if (this.toastTimeoutId) {
      clearTimeout(this.toastTimeoutId);
      this.toastTimeoutId = null;
    }
  }

  private async handleCopy() {
    const content = this.content;
    if (!content || this.copySuccess) {
      return;
    }

    try {
      await navigator.clipboard.writeText(content);

      // Clear any existing timeouts
      this.clearTimeouts();

      // Set success state
      this.copySuccess = true;
      this.showToast = true;

      // Reset success state after duration
      this.successTimeoutId = window.setTimeout(() => {
        this.copySuccess = false;
        this.successTimeoutId = null;
      }, SUCCESS_DURATION_MS);

      // Hide toast after duration
      this.toastTimeoutId = window.setTimeout(() => {
        this.showToast = false;
        this.toastTimeoutId = null;
      }, SUCCESS_DURATION_MS);
    } catch (err) {
      logger.error("Failed to copy text:", err);
    }
  }
}
